import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Loader2, LogOut } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { formatApiError } from '@/lib/api';
import { CollectionTab } from '@/components/admin/CollectionTab';
import { SettingsTab } from '@/components/admin/SettingsTab';
import { CalculatorTab } from '@/components/admin/CalculatorTab';
import { EnquiriesTab } from '@/components/admin/EnquiriesTab';
import { PORTFOLIO_CATEGORIES } from '@/data/content';

const CATEGORY_OPTIONS = PORTFOLIO_CATEGORIES.filter((c) => c !== 'All');

const TABS = {
  Portfolio: {
    coll: 'portfolio', ctxKey: 'portfolio', columns: ['title', 'category', 'type'],
    fields: [
      { name: 'title', label: 'Event Title', required: true },
      { name: 'category', label: 'Category', type: 'select', options: CATEGORY_OPTIONS },
      { name: 'type', label: 'Media Type', type: 'select', options: ['image', 'video'] },
      { name: 'videoUrl', label: 'Video URL', hint: 'Paste a YouTube, YouTube Shorts or Instagram Reel/Post URL — the platform is auto-detected. Leave empty for photos.' },
      { name: 'caption', label: 'Caption', type: 'textarea', hint: 'Shown under reels and videos on the website.' },
      { name: 'image', label: 'Event Photo', type: 'image', hint: 'Upload from your phone/computer, or paste an image URL. Optional for YouTube videos — the thumbnail is pulled automatically.' },
      { name: 'location', label: 'Location' },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'order', label: 'Sort Order', type: 'number', default: 0 },
      { name: 'visible', label: 'Visible on website', type: 'toggle', default: true },
    ],
  },
  Categories: {
    coll: 'categories', ctxKey: 'categories', columns: ['label', 'slug'],
    fields: [
      { name: 'label', label: 'Category Name', required: true },
      { name: 'slug', label: 'URL Slug', hint: 'Used in shareable links like /portfolio/birthdays. Auto-generated if left empty. Renaming a category updates its events automatically.' },
      { name: 'listed', label: 'Show in public portfolio tabs (unlisted categories still open via direct URL)', type: 'toggle', default: true },
      { name: 'order', label: 'Sort Order', type: 'number', default: 0 },
    ],
  },
  Services: {
    coll: 'services', ctxKey: 'services', columns: ['title', 'slug'],
    fields: [
      { name: 'title', label: 'Service Title', required: true },
      { name: 'slug', label: 'Slug (e.g. weddings)' },
      { name: 'image', label: 'Service Photo', type: 'image', hint: 'Upload a cinematic photo for this service card, or paste an image URL.' },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'order', label: 'Sort Order', type: 'number', default: 0 },
    ],
  },
  Testimonials: {
    coll: 'testimonials', ctxKey: 'testimonials', columns: ['name', 'event'],
    fields: [
      { name: 'name', label: 'Client Name', required: true },
      { name: 'event', label: 'Event Type (e.g. Wedding, Hyderabad)' },
      { name: 'initials', label: 'Initials (e.g. SK)' },
      { name: 'review', label: 'Review', type: 'textarea' },
      { name: 'order', label: 'Sort Order', type: 'number', default: 0 },
    ],
  },
  Awards: {
    coll: 'awards', ctxKey: 'awards', columns: ['title', 'year', 'org'],
    fields: [
      { name: 'title', label: 'Award Title', required: true },
      { name: 'year', label: 'Year' },
      { name: 'org', label: 'Organisation' },
      { name: 'order', label: 'Sort Order', type: 'number', default: 0 },
    ],
  },
  Pricing: {
    coll: 'packages', ctxKey: 'packages', columns: ['name', 'price', 'category'],
    fields: [
      { name: 'name', label: 'Package Name', required: true },
      { name: 'category', label: 'Category', type: 'select', options: [] },
      { name: 'price', label: 'Price (e.g. ₹550 per running ft)' },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'popular', label: "Show 'Most Popular' badge", type: 'toggle', default: false },
      { name: 'order', label: 'Sort Order', type: 'number', default: 0 },
    ],
  },
  'Price Categories': {
    coll: 'package_categories', ctxKey: 'packageCategories', columns: ['label'],
    fields: [
      { name: 'label', label: 'Category Name (e.g. Photography, Gamezone)', required: true },
      { name: 'order', label: 'Sort Order', type: 'number', default: 0 },
    ],
  },
};

const TAB_NAMES = [...Object.keys(TABS), 'Calculator', 'Settings', 'Enquiries'];

const inputCls = 'w-full bg-ink border border-white/15 px-4 py-3 text-sm text-cream placeholder:text-white/30 focus:border-champagne focus:outline-none transition-colors';

export default function Admin() {
  const { user, login, logout } = useAuth();
  const [tab, setTab] = useState('Portfolio');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (user === null) {
    return <div className="min-h-screen bg-ink flex items-center justify-center"><Loader2 className="animate-spin text-champagne" size={28} /></div>;
  }

  if (user === false) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center px-5">
        <form data-testid="admin-login-form" onSubmit={submit} className="w-full max-w-sm border border-white/10 bg-surface p-8">
          <p className="font-serif text-3xl text-cream text-center">Events <span className="italic text-champagne">by</span> AJ</p>
          <p className="mt-2 mb-8 text-center text-[10px] uppercase tracking-[0.3em] text-white/40">Content Manager</p>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1.5">Email</label>
          <input data-testid="admin-email" type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} required />
          <label className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1.5 mt-5">Password</label>
          <input data-testid="admin-password" type="password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} required />
          {error && <p data-testid="admin-login-error" className="mt-4 text-sm text-red-400">{error}</p>}
          <button data-testid="admin-login-btn" type="submit" disabled={busy} className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-champagne text-ink px-6 py-3.5 text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-cream transition-colors disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : null} Sign In
          </button>
          <Link to="/" className="mt-5 block text-center text-[11px] uppercase tracking-[0.2em] text-white/40 hover:text-champagne transition-colors">Back to website</Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink text-cream">
      <header className="sticky top-0 z-40 bg-black/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <p className="font-serif text-xl">Events <span className="italic text-champagne">by</span> AJ <span className="ml-2 text-[10px] uppercase tracking-[0.3em] text-white/40 font-sans">Admin</span></p>
          <div className="flex items-center gap-2">
            <Link data-testid="admin-view-site" to="/" className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 text-[11px] uppercase tracking-[0.2em] text-white/60 hover:text-champagne hover:border-champagne transition-colors">
              <ExternalLink size={13} /> View Site
            </Link>
            <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 text-[11px] uppercase tracking-[0.2em] text-white/60 hover:text-red-400 hover:border-red-400/50 transition-colors">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-5 flex gap-1 overflow-x-auto pb-px" data-testid="admin-tabs">
          {TAB_NAMES.map((t) => (
            <button
              key={t}
              data-testid={`admin-tabbtn-${t.toLowerCase()}`}
              onClick={() => setTab(t)}
              className={`shrink-0 px-4 py-3 text-[11px] uppercase tracking-[0.2em] border-b-2 transition-colors ${
                tab === t ? 'border-champagne text-champagne' : 'border-transparent text-white/50 hover:text-cream'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-5 py-10">
        {TABS[tab] ? <CollectionTab key={TABS[tab].coll} {...TABS[tab]} /> : tab === 'Calculator' ? <CalculatorTab /> : tab === 'Settings' ? <SettingsTab /> : <EnquiriesTab />}
      </main>
    </div>
  );
}
